import React, { useState, useEffect, useCallback, useRef, useMemo, useId } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Drawer,
  Divider,
  Tooltip,
  CircularProgress,
  Skeleton,
  Fade,
  alpha,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Search,
  ContentCopy,
  CheckCircleOutline,
  Close,
  RefreshOutlined,
  PauseCircleOutline,
  PlayCircleOutline,
  OpenInNew,
  VerifiedOutlined,
  ExpandMore,
  WarningAmberRounded,
  BoltRounded,
  CheckCircleRounded,
  ErrorOutlineRounded,
  ScheduleRounded,
  SwapHorizRounded,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';

import { fCurrency } from '../utils/formatNumber';
import { hasPermission, isAdminOrOwner, PERMISSIONS } from '../utils/hasPermission';
import {
  getLiveTransactions,
  getLiveTransactionDetail,
  connectLiveTransactionsStream,
} from '../api/liveTransactions.api';
import {
  refundTransaction,
  completeTransaction,
  getExternalFundingTransaction,
  getExternalPurchaseTransaction,
} from '../api/transactions.api';

const MAX_LIVE_ROWS = 100;

const TRANSACTION_TYPE_CONFIG = {
  WALLET_FUNDING: { label: 'Wallet Deposit', color: '#2563EB' },
  ADMIN_WALLET_FUNDING: { label: 'Admin Funding', color: '#7C3AED' },
  WALLET_DEDUCTING: { label: 'Admin Deduct', color: '#DC2626' },
  TRANSACTION_REFUND: { label: 'Order Refund', color: '#6366F1' },
  WALLET_TRANSFER: { label: 'Wallet Transfer', color: '#0D9488' },
  COMMISSION_WITHDRAWAL: { label: 'Commission', color: '#059669' },
  CASHBACK_REWARD: { label: 'Cashback', color: '#10B981' },
  REFERRAL_BONUS: { label: 'Referral Bonus', color: '#8B5CF6' },
  DATA_ORDER: { label: 'Data', color: '#0891B2' },
  API_DATA_ORDER: { label: 'API Data', color: '#0891B2' },
  AIRTIME_ORDER: { label: 'Airtime', color: '#059669' },
  API_AIRTIME_ORDER: { label: 'API Airtime', color: '#059669' },
  ELECTRICITY_ORDER: { label: 'Electricity', color: '#D97706' },
  API_ELECTRICITY_ORDER: { label: 'API Electricity', color: '#D97706' },
  CABLE_ORDER: { label: 'TV / Cable', color: '#9333EA' },
  API_CABLE_ORDER: { label: 'API Cable', color: '#9333EA' },
  RESULT_CHECK_ORDER: { label: 'Exam PIN', color: '#4F46E5' },
  API_RESULT_CHECK_ORDER: { label: 'API Exam PIN', color: '#4F46E5' },
};

const TABS = [
  { id: 'all', label: 'All Activity' },
  { id: 'checkouts', label: 'VTU Checkouts' },
  { id: 'in_flight', label: 'In-Flight & Pending' },
  { id: 'deposits', label: 'Deposits' },
  { id: 'attention', label: 'Needs Attention' },
];

function getTxTypeMeta(txType) {
  return (
    TRANSACTION_TYPE_CONFIG[txType] || {
      label: String(txType || 'Transaction').replace(/_/g, ' '),
      color: '#475569',
    }
  );
}

function renderTimelineIcon(step) {
  const stepStatus = String(step?.status || '').toUpperCase();
  if (stepStatus === 'SUCCESS') {
    return <CheckCircleRounded sx={{ fontSize: 16, color: '#059669' }} />;
  }
  if (stepStatus === 'RUNNING' || stepStatus === 'PENDING') {
    return <ScheduleRounded sx={{ fontSize: 16, color: '#0284C7' }} />;
  }
  return <ErrorOutlineRounded sx={{ fontSize: 16, color: '#DC2626' }} />;
}

function matchesActiveFilters(tx, { tab, statusFilter, categoryFilter, channelFilter, search }) {
  if (!tx) return false;
  const txType = String(tx.transactionType || '');
  const status = String(tx.status || '').toLowerCase();

  if (tab === 'checkouts' && !txType.endsWith('_ORDER')) return false;
  if (tab === 'in_flight' && status !== 'pending') return false;
  if (tab === 'deposits' && txType !== 'WALLET_FUNDING' && txType !== 'ADMIN_WALLET_FUNDING') {
    return false;
  }
  if (tab === 'attention') {
    const ageMs = tx.createdAt ? Date.now() - new Date(tx.createdAt).getTime() : 0;
    const isStuckPending = status === 'pending' && ageMs >= 5 * 60 * 1000;
    if (!tx.manualReconciliationNeeded && status !== 'failed' && !isStuckPending) {
      return false;
    }
  }

  if (statusFilter && status !== statusFilter.toLowerCase()) return false;

  if (categoryFilter) {
    const cat = `${tx.category || ''} ${tx.service || ''}`.toLowerCase();
    if (!cat.includes(categoryFilter.toLowerCase())) return false;
  }

  if (channelFilter && String(tx.channel || '').toLowerCase() !== channelFilter.toLowerCase()) {
    return false;
  }

  if (search && search.trim()) {
    const q = search.trim().toLowerCase();
    const haystack = [
      tx.ref,
      tx.txId,
      tx.clientRef,
      tx.vendorRef,
      tx.target,
      tx.name,
      tx.serviceName,
      tx.description,
      tx.providerSlug,
      tx.processor,
      tx.user?.username,
      tx.user?.fullName,
      tx.user?.email,
      tx.user?.phone,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (!haystack.includes(q)) return false;
  }

  return true;
}

const CopyIconBtn = ({ value, tooltip = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(String(value));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Tooltip title={copied ? 'Copied!' : tooltip}>
      <IconButton size="small" onClick={handleCopy} sx={{ p: 0.4 }}>
        {copied ? (
          <CheckCircleOutline sx={{ fontSize: 14, color: '#059669' }} />
        ) : (
          <ContentCopy sx={{ fontSize: 13, color: 'text.disabled' }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

const DetailRow = ({ label, value, mono = false, copyable = false, valueColor }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    spacing={1.5}
    sx={{
      py: 1.1,
      px: 1.5,
      '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.02)' },
    }}
  >
    <Typography fontSize="0.82rem" color="text.secondary" fontWeight={500}>
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography
        fontSize="0.85rem"
        fontWeight={600}
        color={valueColor || 'text.primary'}
        sx={{
          fontFamily: mono ? '"SF Mono", "Fira Code", Consolas, monospace' : 'inherit',
          maxWidth: 250,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'right',
        }}
      >
        {value !== undefined && value !== null && value !== '' ? value : '—'}
      </Typography>
      {copyable && value ? <CopyIconBtn value={value} /> : null}
    </Stack>
  </Stack>
);

const AttemptPayloadViewer = ({ payload }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!payload || (typeof payload === 'object' && Object.keys(payload).length === 0)) return null;

  let formatted = '';
  try {
    formatted = typeof payload === 'string' ? JSON.stringify(JSON.parse(payload), null, 2) : JSON.stringify(payload, null, 2);
  } catch {
    formatted = String(payload);
  }

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(formatted);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box sx={{ mt: 0.6 }}>
      <Stack
        direction="row"
        spacing={0.6}
        alignItems="center"
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          cursor: 'pointer',
          width: 'fit-content',
          py: 0.25,
          px: 0.9,
          borderRadius: '6px',
          bgcolor: '#F1F5F9',
          transition: 'all 0.15s ease',
          '&:hover': { bgcolor: '#E2E8F0' },
        }}
      >
        <Typography fontSize="0.71rem" fontWeight={700} color="#0369A1">
          {open ? 'Hide Provider Payload' : 'View Outbound Payload'}
        </Typography>
        <ExpandMore
          sx={{
            fontSize: 14,
            color: '#0369A1',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}
        />
      </Stack>
      {open && (
        <Paper
          elevation={0}
          sx={{
            mt: 0.8,
            p: 1.5,
            borderRadius: '10px',
            bgcolor: '#0F172A',
            color: '#E2E8F0',
            fontFamily: '"SF Mono", "Fira Code", Consolas, monospace',
            fontSize: '0.71rem',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 200,
            overflowY: 'auto',
            position: 'relative',
          }}
        >
          <Box sx={{ position: 'absolute', top: 6, right: 6 }}>
            <Tooltip title={copied ? 'Copied!' : 'Copy Payload'}>
              <IconButton size="small" onClick={handleCopy} sx={{ color: '#94A3B8', '&:hover': { color: '#FFFFFF' } }}>
                {copied ? <CheckCircleOutline sx={{ fontSize: 14, color: '#10B981' }} /> : <ContentCopy sx={{ fontSize: 14 }} />}
              </IconButton>
            </Tooltip>
          </Box>
          {formatted}
        </Paper>
      )}
    </Box>
  );
};

const ApiResponseViewer = ({ response, requestPayload }) => {
  const [tab, setTab] = useState('request'); // 'request' | 'response'
  const [expanded, setExpanded] = useState(true);
  const [copied, setCopied] = useState(false);

  // Normalize request payload
  let reqData = requestPayload || response?.request_payload || null;
  if (!reqData && Array.isArray(response?.attemptLogs) && response.attemptLogs.length > 0) {
    reqData = response.attemptLogs[0]?.requestPayload || null;
  }

  // Normalize response payload
  let resData = response?.api_response || response?.vendorResponse || response;
  if (resData && typeof resData === 'object' && resData.api_response) {
    resData = resData.api_response;
  }

  const hasRequestData = Boolean(reqData && (typeof reqData !== 'object' || Object.keys(reqData).length > 0));
  const hasResponseData = Boolean(resData && (typeof resData !== 'object' || Object.keys(resData).length > 0));

  if (!hasRequestData && !hasResponseData) return null;

  // Auto-switch to response tab if request is absent
  const activeTab = tab === 'request' && !hasRequestData && hasResponseData ? 'response' : tab;
  const activeData = activeTab === 'request'
    ? (reqData || { message: 'No outbound request payload captured' })
    : (resData || { message: 'No vendor response captured' });

  let formatted = '';
  try {
    const parsed = typeof activeData === 'string' ? JSON.parse(activeData) : activeData;
    formatted = JSON.stringify(parsed, null, 2);
  } catch {
    formatted = String(activeData);
  }

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(formatted);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '14px',
        border: '1px solid #E2E8F0',
        overflow: 'hidden',
        mt: 2.5,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: '#F8FAFC',
          borderBottom: '1px solid #E2E8F0',
        }}
      >
        <Stack direction="row" spacing={0.8} alignItems="center">
          <Button
            size="small"
            variant={activeTab === 'request' ? 'contained' : 'text'}
            onClick={() => { setTab('request'); setExpanded(true); }}
            sx={{
              textTransform: 'none',
              fontSize: '0.74rem',
              fontWeight: 700,
              borderRadius: '8px',
              py: 0.35,
              px: 1.2,
              minWidth: 'auto',
              boxShadow: 'none',
              ...(activeTab === 'request'
                ? { bgcolor: '#0284C7', color: '#FFFFFF', '&:hover': { bgcolor: '#0369A1' } }
                : { color: 'text.secondary', '&:hover': { bgcolor: '#E2E8F0' } }),
            }}
          >
            Outbound Request Payload
          </Button>
          <Button
            size="small"
            variant={activeTab === 'response' ? 'contained' : 'text'}
            onClick={() => { setTab('response'); setExpanded(true); }}
            sx={{
              textTransform: 'none',
              fontSize: '0.74rem',
              fontWeight: 700,
              borderRadius: '8px',
              py: 0.35,
              px: 1.2,
              minWidth: 'auto',
              boxShadow: 'none',
              ...(activeTab === 'response'
                ? { bgcolor: '#0284C7', color: '#FFFFFF', '&:hover': { bgcolor: '#0369A1' } }
                : { color: 'text.secondary', '&:hover': { bgcolor: '#E2E8F0' } }),
            }}
          >
            Vendor API Response
          </Button>
        </Stack>

        <Stack direction="row" spacing={0.4} alignItems="center">
          <Tooltip title={copied ? 'Copied!' : 'Copy Active JSON'}>
            <IconButton size="small" onClick={handleCopy} sx={{ color: 'text.secondary' }}>
              {copied ? <CheckCircleOutline sx={{ fontSize: 16, color: '#10B981' }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => setExpanded((prev) => !prev)} sx={{ color: 'text.secondary' }}>
            <ExpandMore
              sx={{
                fontSize: 18,
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </IconButton>
        </Stack>
      </Stack>

      {expanded && (
        <Fade in={expanded}>
          <Box
            sx={{
              p: 2,
              bgcolor: '#0F172A',
              color: '#E2E8F0',
              fontFamily: '"SF Mono", "Fira Code", Consolas, monospace',
              fontSize: '0.73rem',
              lineHeight: 1.55,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 280,
              overflowY: 'auto',
            }}
          >
            {formatted}
          </Box>
        </Fade>
      )}
    </Paper>
  );
};

const LiveStatusChip = ({
  status,
  manualReconciliationNeeded,
  refund,
  liveRouting,
  reconciliationAttempt,
}) => {
  if (manualReconciliationNeeded) {
    return (
      <Chip
        size="small"
        icon={<WarningAmberRounded sx={{ fontSize: '14px !important', color: '#B45309 !important' }} />}
        label="Manual Recon Needed"
        sx={{
          fontWeight: 700,
          fontSize: '0.73rem',
          bgcolor: '#FEF3C7',
          color: '#B45309',
          border: '1px solid rgba(180, 83, 9, 0.3)',
          height: 26,
        }}
      />
    );
  }

  const reconAttempt = liveRouting?.reconciliationAttempt || reconciliationAttempt;
  const isQueuedRecon = liveRouting?.state === 'queued_reconciliation' || Boolean(reconAttempt);

  if (status === 'pending' && isQueuedRecon) {
    const isAttempt2 = reconAttempt === 2;
    const label = isAttempt2 ? 'Queue Check #2 (~5m)' : 'Queue Check #1 (~1m)';
    return (
      <Chip
        size="small"
        icon={<ScheduleRounded sx={{ fontSize: '13px !important', color: '#D97706 !important', ml: '6px !important' }} />}
        label={label}
        sx={{
          fontWeight: 700,
          fontSize: '0.73rem',
          bgcolor: '#FFFBEB',
          color: '#B45309',
          border: '1px solid rgba(180, 83, 9, 0.25)',
          height: 26,
        }}
      />
    );
  }

  if (status === 'pending' && liveRouting) {
    const isBg = liveRouting.backgroundHandoff || liveRouting.state === 'background_settlement';
    return (
      <Chip
        size="small"
        icon={
          <CircularProgress
            size={11}
            thickness={5}
            sx={{ color: isBg ? '#7C3AED !important' : '#0284C7 !important', ml: '6px !important' }}
          />
        }
        label={isBg ? 'Background Settle' : 'Routing Live'}
        sx={{
          fontWeight: 700,
          fontSize: '0.73rem',
          bgcolor: isBg ? '#F3E8FF' : '#E0F2FE',
          color: isBg ? '#6D28D9' : '#0369A1',
          border: `1px solid ${isBg ? 'rgba(109,40,217,0.25)' : 'rgba(3,105,161,0.25)'}`,
          height: 26,
        }}
      />
    );
  }

  const normalized = String(status || 'pending').toLowerCase();
  const styles = {
    success: { bg: '#ECFDF5', color: '#059669', label: 'Success' },
    completed: { bg: '#ECFDF5', color: '#059669', label: 'Completed' },
    pending: { bg: '#FFFBEB', color: '#D97706', label: 'Pending' },
    failed: { bg: '#FEF2F2', color: '#DC2626', label: refund ? 'Failed · Refunded' : 'Failed' },
    abandoned: { bg: '#F1F5F9', color: '#64748B', label: 'Abandoned' },
  };
  const cfg = styles[normalized] || styles.pending;

  return (
    <Chip
      size="small"
      label={cfg.label}
      sx={{
        fontWeight: 700,
        fontSize: '0.73rem',
        bgcolor: cfg.bg,
        color: cfg.color,
        border: `1px solid ${alpha(cfg.color, 0.22)}`,
        height: 26,
      }}
    />
  );
};

const RouteBadge = ({ tx }) => {
  const live = tx.liveRouting;
  if (live && tx.status === 'pending') {
    const cur = live.currentRoute;
    if (cur) {
      let label = `Trying ${cur.providerName || cur.processorSlug} #${cur.routeIndex + 1}`;
      if (cur.isFailover) {
        label = `Failover -> ${cur.providerName || cur.processorSlug} #${cur.routeIndex + 1}`;
      } else if (cur.isRetry) {
        label = `Retry ${cur.attempt}/${cur.totalAttempts} · ${cur.providerName || cur.processorSlug}`;
      }
      return (
        <Stack direction="row" spacing={0.6} alignItems="center">
          <BoltRounded sx={{ fontSize: 15, color: cur.isFailover ? '#D97706' : '#0284C7' }} />
          <Typography fontSize="0.78rem" fontWeight={700} color={cur.isFailover ? '#B45309' : '#0369A1'}>
            {label}
          </Typography>
        </Stack>
      );
    }
    return (
      <Typography fontSize="0.78rem" fontWeight={600} color="#0369A1">
        Dispatching router...
      </Typography>
    );
  }

  const attempts = Array.isArray(tx.attemptSummary) ? tx.attemptSummary : [];
  const slug = tx.providerSlug || (tx.processor !== 'vtu-v2' ? tx.processor : null);

  if (attempts.length > 1) {
    const last = attempts[attempts.length - 1];
    return (
      <Stack spacing={0.2}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <SwapHorizRounded sx={{ fontSize: 14, color: '#D97706' }} />
          <Typography fontSize="0.79rem" fontWeight={700} color="text.primary">
            {slug || last?.processorSlug || 'vtu-v2'}
          </Typography>
        </Stack>
        <Typography fontSize="0.7rem" color="#B45309" fontWeight={600}>
          {attempts.length} tries{last?.durationMs ? ` · ${last.durationMs}ms` : ''}
        </Typography>
      </Stack>
    );
  }

  if (slug) {
    const singleDuration = attempts[0]?.durationMs;
    return (
      <Stack spacing={0.1}>
        <Typography fontSize="0.8rem" fontWeight={600} color="text.primary">
          {slug}
        </Typography>
        {singleDuration ? (
          <Typography fontSize="0.7rem" color="text.secondary">
            {singleDuration}ms
          </Typography>
        ) : null}
      </Stack>
    );
  }

  if (tx.isManual) {
    return (
      <Typography fontSize="0.78rem" color="#7C3AED" fontWeight={600}>
        Manual Queue
      </Typography>
    );
  }

  return (
    <Typography fontSize="0.78rem" color="text.secondary">
      {tx.payment_method || 'Wallet'}
    </Typography>
  );
};

export default function LiveTransactionsPage() {
  const tableCaptionId = useId();
  const { user: adminUser, activeApp } = useSelector((state) => state.auth);
  const canAccessLive = isAdminOrOwner(adminUser, activeApp);

  const [transactions, setTransactions] = useState([]);
  const [inFlight, setInFlight] = useState([]);
  const [metrics, setMetrics] = useState({
    inFlightCount: 0,
    totalToday: 0,
    ordersToday: 0,
    successOrdersToday: 0,
    pendingOrdersToday: 0,
    failedOrdersToday: 0,
    successRate: 100,
    checkoutVolume: 0,
    estimatedProfit: 0,
    depositVolume: 0,
    depositCount: 0,
    needsAttentionCount: 0,
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [connectionMode, setConnectionMode] = useState('connecting');
  const [isPaused, setIsPaused] = useState(false);
  const [pausedCount, setPausedCount] = useState(0);

  // Filters
  const [activeTab, setActiveTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Row flash animation tracking
  const [flashedRefs, setFlashedRefs] = useState({});
  const [nowTick, setNowTick] = useState(Date.now());

  // Slide-over Inspector Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [externalResult, setExternalResult] = useState(null);

  const lastEventIdRef = useRef(null);
  const lastUpdatedCursorRef = useRef(null);
  const isPausedRef = useRef(false);
  const pausedQueueRef = useRef([]);
  const filterStateRef = useRef({
    tab: activeTab,
    statusFilter,
    categoryFilter,
    channelFilter,
    search: debouncedSearch,
  });
  const selectedRefTracker = useRef({ id: null, ref: null });

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    filterStateRef.current = {
      tab: activeTab,
      statusFilter,
      categoryFilter,
      channelFilter,
      search: debouncedSearch,
    };
  }, [activeTab, statusFilter, categoryFilter, channelFilter, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 280);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Tick every 1s ONLY when there are active in-flight checkouts (for live elapsed timers)
  useEffect(() => {
    if (inFlight.length === 0) return undefined;
    const interval = setInterval(() => {
      setNowTick(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [inFlight.length]);

  const triggerRowFlash = useCallback((refKey, tone = 'info') => {
    if (!refKey) return;
    setFlashedRefs((prev) => ({ ...prev, [refKey]: tone }));
    setTimeout(() => {
      setFlashedRefs((prev) => {
        if (!prev[refKey]) return prev;
        const next = { ...prev };
        delete next[refKey];
        return next;
      });
    }, 1800);
  }, []);

  const loadSnapshot = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      try {
        const res = await getLiveTransactions({
          limit: 60,
          tab: activeTab,
          status: statusFilter || undefined,
          category: categoryFilter || undefined,
          channel: channelFilter || undefined,
          search: debouncedSearch || undefined,
        });

        if (res.ok && res.data?.data) {
          const payload = res.data.data;
          const rows = Array.isArray(payload.transactions) ? payload.transactions : [];
          setTransactions(rows);
          setInFlight(Array.isArray(payload.inFlight) ? payload.inFlight : []);
          if (payload.metrics) {
            setMetrics(payload.metrics);
          }
          if (payload.latestEventId) {
            lastEventIdRef.current = payload.latestEventId;
          }
          if (payload.serverTime) {
            lastUpdatedCursorRef.current = payload.serverTime;
          }
        }
      } catch {
        // Handled gracefully by stream / fallback
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [activeTab, statusFilter, categoryFilter, channelFilter, debouncedSearch]
  );

  useEffect(() => {
    if (!canAccessLive) return;
    loadSnapshot({ silent: false });
  }, [loadSnapshot, canAccessLive]);

  const fetchDetailForSelected = useCallback(async (idOrRef, { quiet = false } = {}) => {
    if (!idOrRef) return;
    if (!quiet) setDetailLoading(true);
    try {
      const res = await getLiveTransactionDetail(idOrRef);
      if (res.ok && res.data?.data) {
        setSelectedDetail(res.data.data);
        setSelectedSummary(res.data.data);
      }
    } finally {
      if (!quiet) setDetailLoading(false);
    }
  }, []);

  const applySingleEnvelope = useCallback(
    (envelope, { enableFlash = true } = {}) => {
      if (!envelope || !envelope.type) return;
      if (envelope.eventId) {
        lastEventIdRef.current = envelope.eventId;
      }
      if (envelope.timestamp) {
        lastUpdatedCursorRef.current = envelope.timestamp;
      }

      const { type, data } = envelope;

      if (type === 'checkout.started' && data?.txRef) {
        setInFlight((prev) => {
          const filtered = prev.filter((item) => item.txRef !== data.txRef);
          return [data, ...filtered];
        });
        setTransactions((prev) =>
          prev.map((tx) => (tx.ref === data.txRef ? { ...tx, liveRouting: data } : tx))
        );
        if (selectedRefTracker.current.ref === data.txRef) {
          setSelectedSummary((prev) => (prev ? { ...prev, liveRouting: data } : prev));
          setSelectedDetail((prev) => (prev ? { ...prev, liveRouting: data } : prev));
        }
        return;
      }

      if (type === 'checkout.attempt' && data?.txRef) {
        const updatedFlight = data.inFlight;
        if (updatedFlight) {
          setInFlight((prev) => {
            const exists = prev.some((item) => item.txRef === data.txRef);
            if (!exists) return [updatedFlight, ...prev];
            return prev.map((item) => (item.txRef === data.txRef ? updatedFlight : item));
          });
          setTransactions((prev) =>
            prev.map((tx) => (tx.ref === data.txRef ? { ...tx, liveRouting: updatedFlight } : tx))
          );
          if (selectedRefTracker.current.ref === data.txRef) {
            setSelectedSummary((prev) => (prev ? { ...prev, liveRouting: updatedFlight } : prev));
            setSelectedDetail((prev) => (prev ? { ...prev, liveRouting: updatedFlight } : prev));
          }
        }
        return;
      }

      if (type === 'checkout.background' && data?.txRef) {
        setInFlight((prev) =>
          prev.map((item) =>
            item.txRef === data.txRef
              ? { ...item, backgroundHandoff: true, state: 'background_settlement' }
              : item
          )
        );
        setTransactions((prev) =>
          prev.map((tx) =>
            tx.ref === data.txRef && tx.liveRouting
              ? {
                  ...tx,
                  liveRouting: {
                    ...tx.liveRouting,
                    backgroundHandoff: true,
                    state: 'background_settlement',
                  },
                }
              : tx
          )
        );
        return;
      }

      if (type === 'transaction.upsert' && data?.transaction) {
        const incomingTx = data.transaction;
        const action = data.action;

        if (incomingTx.ref && incomingTx.status && incomingTx.status !== 'pending') {
          setInFlight((prev) => prev.filter((item) => item.txRef !== incomingTx.ref));
        }

        const shouldShow = matchesActiveFilters(incomingTx, filterStateRef.current);

        setTransactions((prev) => {
          const existingIndex = prev.findIndex(
            (row) =>
              (incomingTx.id && row.id === incomingTx.id) ||
              (incomingTx.ref && row.ref === incomingTx.ref)
          );

          if (!shouldShow) {
            if (existingIndex === -1) return prev;
            return prev.filter((_, idx) => idx !== existingIndex);
          }

          if (existingIndex >= 0) {
            const merged = {
              ...prev[existingIndex],
              ...incomingTx,
              user: incomingTx.user || prev[existingIndex].user || null,
            };
            const copy = [...prev];
            copy[existingIndex] = merged;
            return copy;
          }

          return [incomingTx, ...prev].slice(0, MAX_LIVE_ROWS);
        });

        if (enableFlash && incomingTx.ref) {
          let flashTone = 'info';
          if (incomingTx.status === 'success' || incomingTx.status === 'completed') {
            flashTone = 'success';
          } else if (incomingTx.status === 'failed' || incomingTx.manualReconciliationNeeded) {
            flashTone = 'error';
          }
          triggerRowFlash(incomingTx.ref, flashTone);
        }

        // Keep live pulse counters responsive between snapshots
        if (action === 'created') {
          setMetrics((prev) => {
            const isOrder = String(incomingTx.transactionType || '').endsWith('_ORDER');
            const isDeposit =
              incomingTx.transactionType === 'WALLET_FUNDING' ||
              incomingTx.transactionType === 'ADMIN_WALLET_FUNDING';
            const isDone = incomingTx.status === 'success' || incomingTx.status === 'completed';

            return {
              ...prev,
              totalToday: prev.totalToday + 1,
              ordersToday: isOrder ? prev.ordersToday + 1 : prev.ordersToday,
              pendingOrdersToday:
                isOrder && incomingTx.status === 'pending'
                  ? prev.pendingOrdersToday + 1
                  : prev.pendingOrdersToday,
              successOrdersToday:
                isOrder && isDone ? prev.successOrdersToday + 1 : prev.successOrdersToday,
              checkoutVolume:
                isOrder && isDone
                  ? Math.round((prev.checkoutVolume + Number(incomingTx.amount || 0)) * 100) / 100
                  : prev.checkoutVolume,
              estimatedProfit:
                isOrder && isDone
                  ? Math.round((prev.estimatedProfit + Number(incomingTx.profit || 0)) * 100) / 100
                  : prev.estimatedProfit,
              depositCount: isDeposit && isDone ? prev.depositCount + 1 : prev.depositCount,
              depositVolume:
                isDeposit && isDone
                  ? Math.round((prev.depositVolume + Number(incomingTx.amount || 0)) * 100) / 100
                  : prev.depositVolume,
            };
          });
        } else if (action === 'updated') {
          // Refresh accurate server-side aggregated metrics silently when an order settles
          getLiveTransactions({ limit: 1 }).then((snap) => {
            if (snap.ok && snap.data?.data?.metrics) {
              setMetrics(snap.data.data.metrics);
            }
          }).catch(() => {});
        }

        // If this transaction is open in the inspector drawer, sync it live
        if (
          (incomingTx.id && selectedRefTracker.current.id === incomingTx.id) ||
          (incomingTx.ref && selectedRefTracker.current.ref === incomingTx.ref)
        ) {
          setSelectedSummary((prev) => (prev ? { ...prev, ...incomingTx } : incomingTx));
          setSelectedDetail((prev) => (prev ? { ...prev, ...incomingTx } : prev));
          if (incomingTx.status !== 'pending' && incomingTx.id) {
            fetchDetailForSelected(incomingTx.id, { quiet: true });
          }
        }
      }
    },
    [fetchDetailForSelected, triggerRowFlash]
  );

  // Delta fallback poll handler (used automatically if SSE stream is blocked by a proxy)
  const runFallbackDeltaPoll = useCallback(async () => {
    const res = await getLiveTransactions({
      limit: 40,
      tab: filterStateRef.current.tab,
      status: filterStateRef.current.statusFilter || undefined,
      category: filterStateRef.current.categoryFilter || undefined,
      channel: filterStateRef.current.channelFilter || undefined,
      search: filterStateRef.current.search || undefined,
      updatedAfter: lastUpdatedCursorRef.current || undefined,
    });

    if (!res.ok || !res.data?.data) return;
    const payload = res.data.data;

    if (payload.serverTime) {
      lastUpdatedCursorRef.current = payload.serverTime;
    }

    if (!isPausedRef.current) {
      if (Array.isArray(payload.inFlight)) {
        setInFlight(payload.inFlight);
      }
      if (payload.metrics) {
        setMetrics(payload.metrics);
      }
      if (Array.isArray(payload.transactions) && payload.transactions.length > 0) {
        payload.transactions
          .slice()
          .reverse()
          .forEach((tx) => {
            applySingleEnvelope({
              type: 'transaction.upsert',
              timestamp: payload.serverTime,
              data: { action: 'updated', transaction: tx },
            });
          });
      }
    } else if (Array.isArray(payload.transactions) && payload.transactions.length > 0) {
      payload.transactions.forEach((tx) => {
        pausedQueueRef.current.push({
          type: 'transaction.upsert',
          timestamp: payload.serverTime,
          data: { action: 'updated', transaction: tx },
        });
      });
      setPausedCount(pausedQueueRef.current.length);
    }
  }, [applySingleEnvelope]);

  // Connect authenticated SSE stream on mount (admin & owner only)
  useEffect(() => {
    if (!canAccessLive) return undefined;
    const connection = connectLiveTransactionsStream({
      getLastEventId: () => lastEventIdRef.current,
      onModeChange: (mode) => {
        setConnectionMode(mode);
      },
      onConnected: (info) => {
        if (info?.latestEventId) {
          lastEventIdRef.current = info.latestEventId;
        }
        if (!isPausedRef.current && Array.isArray(info?.inFlight)) {
          setInFlight(info.inFlight);
        }
      },
      onResyncRequired: () => {
        if (!isPausedRef.current) {
          loadSnapshot({ silent: true });
        }
      },
      onEvent: (frame) => {
        const envelope =
          frame.data && frame.data.type
            ? frame.data
            : { eventId: frame.id, type: frame.event, data: frame.data };

        if (isPausedRef.current) {
          pausedQueueRef.current.push(envelope);
          setPausedCount(pausedQueueRef.current.length);
          if (envelope.eventId) {
            lastEventIdRef.current = envelope.eventId;
          }
          return;
        }

        applySingleEnvelope(envelope, { enableFlash: true });
      },
      onFallbackPoll: runFallbackDeltaPoll,
    });

    return () => {
      connection.disconnect();
    };
  }, [applySingleEnvelope, canAccessLive, loadSnapshot, runFallbackDeltaPoll]);

  const handleTogglePause = () => {
    if (isPaused) {
      const queued = pausedQueueRef.current.splice(0, pausedQueueRef.current.length);
      setPausedCount(0);
      setIsPaused(false);
      queued.forEach((env) => applySingleEnvelope(env, { enableFlash: false }));
      loadSnapshot({ silent: true });
    } else {
      setIsPaused(true);
    }
  };

  const handleOpenDrawer = (item) => {
    const idOrRef = item.id || item.orderId || item.ref || item.txRef;
    selectedRefTracker.current = {
      id: item.id || item.orderId || null,
      ref: item.ref || item.txRef || null,
    };
    setExternalResult(null);
    setSelectedSummary(item);
    setSelectedDetail(null);
    setDrawerOpen(true);
    if (idOrRef) {
      fetchDetailForSelected(idOrRef);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    selectedRefTracker.current = { id: null, ref: null };
    setExternalResult(null);
  };

  const activeTx = selectedDetail || selectedSummary;
  const isOrderEligible =
    activeTx && String(activeTx.transactionType || '').includes('ORDER') && activeTx.id;
  const canVerifyExternally =
    activeTx &&
    activeTx.id &&
    (String(activeTx.transactionType || '').includes('ORDER') ||
      activeTx.transactionType === 'WALLET_FUNDING');

  const handleRefund = async () => {
    if (!activeTx?.id) return;
    setRefundLoading(true);
    try {
      const res = await refundTransaction(activeTx.id);
      if (res.ok) {
        toast.success(res.data?.message || 'Customer refunded successfully');
        await fetchDetailForSelected(activeTx.id, { quiet: true });
        loadSnapshot({ silent: true });
      } else {
        toast.error(res.data?.message || 'Unable to process refund');
      }
    } finally {
      setRefundLoading(false);
    }
  };

  const handleManualComplete = async () => {
    if (!activeTx?.id) return;
    setCompleteLoading(true);
    try {
      const res = await completeTransaction(activeTx.id, { status: 'success' });
      if (res.ok) {
        toast.success(res.data?.message || 'Transaction marked completed');
        await fetchDetailForSelected(activeTx.id, { quiet: true });
        loadSnapshot({ silent: true });
      } else {
        toast.error(res.data?.message || 'Unable to complete transaction');
      }
    } finally {
      setCompleteLoading(false);
    }
  };

  const handleVerifyExternally = async () => {
    if (!activeTx?.id) return;
    setVerifyLoading(true);
    try {
      const res =
        activeTx.transactionType === 'WALLET_FUNDING'
          ? await getExternalFundingTransaction(activeTx.id)
          : await getExternalPurchaseTransaction(activeTx.id);
      if (res.ok) {
        toast.success(res.data?.message || 'External status retrieved');
        setExternalResult(res.data?.data || {});
      } else {
        toast.error(res.data?.message || 'Verification failed');
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  const connectionBadge = useMemo(() => {
    if (isPaused) {
      return { label: `Paused${pausedCount ? ` · +${pausedCount}` : ''}`, color: '#475569', bg: '#F1F5F9', pulse: false };
    }
    if (connectionMode === 'streaming') {
      return { label: 'Live · Streaming', color: '#059669', bg: '#ECFDF5', pulse: true };
    }
    if (connectionMode === 'polling') {
      return { label: 'Live · Auto-Polling', color: '#2563EB', bg: '#EFF6FF', pulse: true };
    }
    if (connectionMode === 'reconnecting') {
      return { label: 'Reconnecting...', color: '#D97706', bg: '#FFFBEB', pulse: true };
    }
    return { label: 'Connecting...', color: '#64748B', bg: '#F8FAFC', pulse: false };
  }, [connectionMode, isPaused, pausedCount]);

  // Build route execution timeline for inspector drawer (combines live in-flight attempts + persisted attemptLogs)
  const routeTimeline = useMemo(() => {
    if (!activeTx) return [];
    const liveAttempts = activeTx.liveRouting?.attempts || activeTx.attempts;
    if (Array.isArray(liveAttempts) && liveAttempts.length > 0) {
      return liveAttempts;
    }
    const rawLogs = activeTx.attemptLogs || activeTx.response_data?.attemptLogs;
    if (Array.isArray(rawLogs) && rawLogs.length > 0) {
      return rawLogs.map((log, idx) => ({
        processorSlug: log.processorSlug || 'unknown',
        providerName: log.processorSlug || `Route #${idx + 1}`,
        priority: log.priority ?? idx + 1,
        attempt: idx + 1,
        routeIndex: idx,
        isFailover: idx > 0,
        durationMs: log.durationMs,
        status: log.response?.status || (log.response?.success ? 'SUCCESS' : 'FAILED'),
        errorMessage: log.response?.errorMessage || log.error || null,
        attemptedAt: log.attemptedAt,
        requestPayload: log.requestPayload || null,
        response: log.response || null,
      }));
    }
    if (Array.isArray(activeTx.attemptSummary) && activeTx.attemptSummary.length > 0) {
      return activeTx.attemptSummary.map((item, idx) => ({
        ...item,
        providerName: item.processorSlug,
        routeIndex: idx,
        isFailover: idx > 0,
        requestPayload: item.requestPayload || null,
      }));
    }
    return [];
  }, [activeTx]);

  if (!canAccessLive) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', maxWidth: 540, mx: 'auto', mt: 4 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 5,
            borderRadius: 4,
            border: '1px solid #E2E8F0',
            bgcolor: '#FFFFFF',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" color="error" fontWeight={800} sx={{ mb: 1.5 }}>
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Real-time live transactions and multi-vendor telemetry feeds are restricted to workspace owners and administrators only.
          </Typography>
          <Button
            component={RouterLink}
            to="/dashboard/transaction"
            variant="contained"
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
          >
            Go to All Transactions
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        pb: 6,
        maxWidth: '1360px',
        mx: 'auto',
        '@keyframes livePulseDot': {
          '0%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(5, 150, 105, 0.55)' },
          '70%': { transform: 'scale(1)', boxShadow: '0 0 0 7px rgba(5, 150, 105, 0)' },
          '100%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(5, 150, 105, 0)' },
        },
      }}
    >
      {/* ─── Header Bar ───────────────────────────────────────────────── */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 3, mt: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.75}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #059669 0%, #0284C7 100%)',
              boxShadow: '0 6px 16px rgba(5, 150, 105, 0.25)',
            }}
          >
            <BoltRounded sx={{ color: '#FFFFFF', fontSize: 24 }} />
          </Box>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.2}>
              <Typography fontWeight={800} fontSize="1.45rem" lineHeight={1.2}>
                Live Transactions
              </Typography>
              <Box
                role="status"
                aria-live="polite"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.8,
                  px: 1.2,
                  py: 0.4,
                  borderRadius: '999px',
                  bgcolor: connectionBadge.bg,
                  border: `1px solid ${alpha(connectionBadge.color, 0.25)}`,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: connectionBadge.color,
                    animation: connectionBadge.pulse ? 'livePulseDot 2s infinite' : 'none',
                  }}
                />
                <Typography fontSize="0.73rem" fontWeight={700} color={connectionBadge.color}>
                  {connectionBadge.label}
                </Typography>
              </Box>
            </Stack>
            <Typography fontSize="0.82rem" color="text.secondary" sx={{ mt: 0.25 }}>
              Real-time ledger stream, vendor route failovers, and instant reconciliation controls
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.2} alignItems="center" flexWrap="wrap">
          <Button
            size="small"
            variant={isPaused ? 'contained' : 'outlined'}
            color={isPaused ? 'warning' : 'inherit'}
            startIcon={isPaused ? <PlayCircleOutline /> : <PauseCircleOutline />}
            onClick={handleTogglePause}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              borderColor: '#CBD5E1',
              px: 1.8,
            }}
          >
            {(() => {
              if (!isPaused) return 'Pause Feed';
              if (pausedCount > 0) return `Resume (+${pausedCount} new)`;
              return 'Resume Stream';
            })()}
          </Button>

          <Tooltip title="Sync snapshot now">
            <IconButton
              size="small"
              onClick={() => loadSnapshot({ silent: true })}
              sx={{
                border: '1px solid #E2E8F0',
                borderRadius: '10px',
                p: 0.9,
                bgcolor: '#FFFFFF',
              }}
            >
              <RefreshOutlined
                sx={{
                  fontSize: 19,
                  animation: refreshing ? 'spin 0.9s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
          </Tooltip>

          <Button
            component={RouterLink}
            to="/dashboard/transaction"
            size="small"
            variant="outlined"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#E2E8F0',
              color: 'text.primary',
              bgcolor: '#FFFFFF',
              px: 1.8,
            }}
          >
            Historical Ledger
          </Button>
        </Stack>
      </Stack>

      {/* ─── Real-Time KPI Pulse Strip ────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(5, 1fr)',
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: '14px',
            border: '1px solid #E2E8F0',
            background:
              inFlight.length > 0
                ? 'linear-gradient(135deg, #EFF6FF 0%, #E0F2FE 100%)'
                : '#FFFFFF',
          }}
        >
          <Typography fontSize="0.72rem" fontWeight={700} color="text.secondary" textTransform="uppercase">
            In-Flight Now
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.6 }}>
            <Typography fontSize="1.55rem" fontWeight={800} color="#0284C7">
              {inFlight.length}
            </Typography>
            <Typography fontSize="0.76rem" color="text.secondary" fontWeight={500}>
              active · {metrics.pendingOrdersToday || 0} pending
            </Typography>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: 2, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
          <Typography fontSize="0.72rem" fontWeight={700} color="text.secondary" textTransform="uppercase">
            Today&apos;s VTU Orders
          </Typography>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mt: 0.6 }}>
            <Typography fontSize="1.55rem" fontWeight={800} color="text.primary">
              {metrics.ordersToday || 0}
            </Typography>
            <Chip
              size="small"
              label={`${metrics.successRate ?? 100}% success`}
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 700,
                bgcolor: '#ECFDF5',
                color: '#059669',
              }}
            />
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: 2, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
          <Typography fontSize="0.72rem" fontWeight={700} color="text.secondary" textTransform="uppercase">
            Checkout Volume (Today)
          </Typography>
          <Typography fontSize="1.35rem" fontWeight={800} color="text.primary" sx={{ mt: 0.6 }}>
            ₦{fCurrency(metrics.checkoutVolume || 0)}
          </Typography>
          <Typography fontSize="0.74rem" fontWeight={700} color="#059669">
            Est. Margin: +₦{fCurrency(metrics.estimatedProfit || 0)}
          </Typography>
        </Paper>

        <Paper elevation={0} sx={{ p: 2, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
          <Typography fontSize="0.72rem" fontWeight={700} color="text.secondary" textTransform="uppercase">
            Deposits In (Today)
          </Typography>
          <Typography fontSize="1.35rem" fontWeight={800} color="#2563EB" sx={{ mt: 0.6 }}>
            ₦{fCurrency(metrics.depositVolume || 0)}
          </Typography>
          <Typography fontSize="0.74rem" color="text.secondary" fontWeight={500}>
            {metrics.depositCount || 0} wallet top-ups
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          onClick={() => setActiveTab('attention')}
          sx={{
            p: 2,
            borderRadius: '14px',
            border: `1px solid ${metrics.needsAttentionCount > 0 ? '#FCD34D' : '#E2E8F0'}`,
            bgcolor: metrics.needsAttentionCount > 0 ? '#FFFBEB' : '#FFFFFF',
            cursor: 'pointer',
            transition: 'transform 0.15s',
            '&:hover': { transform: 'translateY(-1px)' },
          }}
        >
          <Typography fontSize="0.72rem" fontWeight={700} color="text.secondary" textTransform="uppercase">
            Needs Attention
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.6 }}>
            <Typography
              fontSize="1.55rem"
              fontWeight={800}
              color={metrics.needsAttentionCount > 0 ? '#D97706' : 'text.primary'}
            >
              {metrics.needsAttentionCount || 0}
            </Typography>
            <Typography fontSize="0.75rem" color="text.secondary">
              failed / flagged
            </Typography>
          </Stack>
        </Paper>
      </Box>

      {/* ─── Active In-Flight Vendor Routing Strip ────────────────────── */}
      {inFlight.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: '16px',
            border: '1px solid #BAE6FD',
            background: 'linear-gradient(180deg, #F0F9FF 0%, #FFFFFF 100%)',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={14} thickness={5} sx={{ color: '#0284C7' }} />
              <Typography fontSize="0.84rem" fontWeight={800} color="#0369A1">
                Active Checkout Dispatches ({inFlight.length})
              </Typography>
            </Stack>
            <Typography fontSize="0.74rem" color="text.secondary">
              Click any card to watch live vendor route hops
            </Typography>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 1.5,
            }}
          >
            {inFlight.map((flight) => {
              const elapsedSec = Math.max(
                0,
                Math.round(((nowTick - new Date(flight.startedAt).getTime()) / 1000) * 10) / 10
              );
              const cur = flight.currentRoute;
              let routeText = 'Selecting priority route...';
              if (flight.backgroundHandoff) {
                routeText = 'Background Settlement (15s+)';
              } else if (cur) {
                const prefix = cur.isFailover ? 'Failover -> ' : 'Trying ';
                routeText = `${prefix}${cur.providerName || cur.processorSlug} #${cur.routeIndex + 1}`;
              }

              return (
                <Paper
                  key={flight.txRef}
                  elevation={0}
                  onClick={() => handleOpenDrawer(flight)}
                  sx={{
                    p: 1.6,
                    borderRadius: '12px',
                    border: '1px solid #E2E8F0',
                    bgcolor: '#FFFFFF',
                    cursor: 'pointer',
                    '&:hover': { borderColor: '#38BDF8', boxShadow: '0 4px 12px rgba(2,132,199,0.08)' },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography fontSize="0.82rem" fontWeight={700} noWrap sx={{ maxWidth: '70%' }}>
                      {flight.serviceName || flight.category}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${elapsedSec.toFixed(1)}s`}
                      sx={{
                        height: 20,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        bgcolor: elapsedSec >= 12 ? '#FEF3C7' : '#E0F2FE',
                        color: elapsedSec >= 12 ? '#B45309' : '#0369A1',
                      }}
                    />
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.6 }}>
                    <Typography fontSize="0.76rem" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {flight.target || '—'} · @{flight.user?.username || flight.username || `user_${flight.userId}`}
                    </Typography>
                    <Typography fontSize="0.82rem" fontWeight={800}>
                      ₦{fCurrency(flight.amount || 0)}
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 1 }}>
                    <BoltRounded
                      sx={{
                        fontSize: 14,
                        color: cur?.isFailover ? '#D97706' : '#0284C7',
                      }}
                    />
                    <Typography
                      fontSize="0.74rem"
                      fontWeight={700}
                      color={cur?.isFailover ? '#B45309' : '#0369A1'}
                      noWrap
                    >
                      {routeText}
                    </Typography>
                  </Stack>
                </Paper>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* ─── Filter Tabs & Instant Search ─────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
          mb: 2.5,
          overflow: 'hidden',
        }}
      >
        <Stack
          direction="row"
          spacing={0.8}
          sx={{
            px: 2,
            pt: 1.5,
            pb: 1,
            borderBottom: '1px solid #F1F5F9',
            overflowX: 'auto',
          }}
        >
          {TABS.map((tabItem) => {
            const active = activeTab === tabItem.id;
            return (
              <Button
                key={tabItem.id}
                size="small"
                onClick={() => setActiveTab(tabItem.id)}
                sx={{
                  borderRadius: '9px',
                  textTransform: 'none',
                  fontWeight: active ? 700 : 600,
                  fontSize: '0.8rem',
                  px: 1.6,
                  py: 0.7,
                  whiteSpace: 'nowrap',
                  color: active ? '#2563EB' : 'text.secondary',
                  bgcolor: active ? alpha('#2563EB', 0.1) : 'transparent',
                  '&:hover': { bgcolor: alpha('#2563EB', 0.06) },
                }}
              >
                {tabItem.label}
              </Button>
            );
          })}
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'center' }}
          sx={{ px: 2, py: 1.5 }}
        >
          <TextField
            size="small"
            placeholder="Search reference, phone/meter, customer username, or vendor slug..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 19, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: '#F8FAFC',
              },
            }}
          />

          <Stack direction="row" spacing={1.2} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 135 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ borderRadius: '10px', bgcolor: '#F8FAFC' }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                sx={{ borderRadius: '10px', bgcolor: '#F8FAFC' }}
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="Data">Data</MenuItem>
                <MenuItem value="Airtime">Airtime</MenuItem>
                <MenuItem value="Electricity">Electricity</MenuItem>
                <MenuItem value="Cable">Cable TV</MenuItem>
                <MenuItem value="Result">Exam PIN</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Channel</InputLabel>
              <Select
                label="Channel"
                value={channelFilter}
                onChange={(e) => setChannelFilter(e.target.value)}
                sx={{ borderRadius: '10px', bgcolor: '#F8FAFC' }}
              >
                <MenuItem value="">All Channels</MenuItem>
                <MenuItem value="private">App / Web</MenuItem>
                <MenuItem value="api">Reseller API</MenuItem>
                <MenuItem value="webhook">Bank Webhook</MenuItem>
                <MenuItem value="admin">Admin Action</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Paper>

      {/* ─── Self-Contained Live Feed Region (CSS Containment + Container-Query Responsive Table) ─── */}
      <Paper
        elevation={0}
        className="live-feed-containment-region"
        sx={{
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
          overflow: 'hidden',
          contentVisibility: 'auto',
          containIntrinsicSize: 'auto 300px auto 800px',
          '@supports not (content-visibility: auto)': {
            contain: 'layout style paint',
          },
        }}
      >
        {loading && (
          <Stack spacing={1.5} sx={{ p: 3 }}>
            {[...Array(7)].map((_, idx) => (
              <Skeleton key={idx} variant="rounded" height={54} sx={{ borderRadius: '10px' }} />
            ))}
          </Stack>
        )}
        {!loading && transactions.length === 0 && (
          <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 8, px: 3 }}>
            <Typography fontWeight={700} fontSize="1rem" color="text.primary">
              No transactions match this live view yet
            </Typography>
            <Typography fontSize="0.84rem" color="text.secondary" textAlign="center">
              New checkouts, wallet deposits, and reconciler updates will stream here automatically in real time.
            </Typography>
          </Stack>
        )}
        {!loading && transactions.length > 0 && (
          <Box
            tabIndex={0}
            role="region"
            aria-labelledby={tableCaptionId}
            className="live-tx-table-wrapper"
            sx={{
              overflow: 'auto',
              maxInlineSize: '100%',
              maxBlockSize: 'min(680px, 76vh)',
              containerType: 'inline-size',
              '&:focus-visible': {
                outline: '2px solid #2563EB',
                outlineOffset: '-2px',
              },
              '& table': {
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0,
                '--label-2': '"Customer"',
                '--label-3': '"Service & Target"',
                '--label-4': '"Vendor Route"',
                '--label-5': '"Amount & Margin"',
                '--label-6': '"Status"',
              },
              '& caption': {
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                border: 0,
              },
              '& thead': {
                position: 'sticky',
                insetBlockStart: 0,
                zIndex: 2,
                backgroundColor: '#F8FAFC',
              },
              '& thead th': {
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: '0.73rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#64748B',
                backgroundColor: '#F8FAFC',
                borderBottom: '1px solid #E2E8F0',
                whiteSpace: 'nowrap',
              },
              '& tbody tr': {
                cursor: 'pointer',
                transition: 'background-color 0.35s ease',
              },
              '& tbody th, & tbody td': {
                padding: '13px 16px',
                borderBottom: '1px solid #F1F5F9',
                verticalAlign: 'middle',
                textAlign: 'left',
              },
              '& tbody th': {
                position: 'sticky',
                insetInlineStart: 0,
                zIndex: 1,
                backgroundColor: '#FFFFFF',
                fontWeight: 600,
              },
              '& tbody tr:hover td, & tbody tr:hover th': {
                backgroundColor: '#F8FAFC',
              },
              '@container (width < 680px)': {
                '& table, & thead, & tbody, & tr, & th, & td': {
                  display: 'block',
                },
                '& thead': {
                  display: 'none',
                },
                '& tbody tr': {
                  margin: '12px',
                  border: '1px solid #E2E8F0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                },
                '& tbody th': {
                  position: 'sticky',
                  insetBlockStart: 0,
                  zIndex: 2,
                  backgroundColor: '#F8FAFC',
                  borderBottom: '1px solid #E2E8F0',
                },
                '& tbody td': {
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                },
                '& tbody td::before': {
                  fontWeight: 700,
                  fontSize: '0.74rem',
                  color: '#64748B',
                  flexShrink: 0,
                },
                '& tbody td:nth-child(2)::before': { content: 'var(--label-2) ": "' },
                '& tbody td:nth-child(3)::before': { content: 'var(--label-3) ": "' },
                '& tbody td:nth-child(4)::before': { content: 'var(--label-4) ": "' },
                '& tbody td:nth-child(5)::before': { content: 'var(--label-5) ": "' },
                '& tbody td:nth-child(6)::before': { content: 'var(--label-6) ": "' },
              },
            }}
          >
            <table>
              <caption id={tableCaptionId}>
                Live tenant transactions and real-time checkout routing feed
              </caption>
              <thead>
                <tr>
                  <th scope="col">Time &amp; Ref</th>
                  <th scope="col">Customer</th>
                  <th scope="col">Service &amp; Target</th>
                  <th scope="col">Vendor Route</th>
                  <th scope="col">Amount &amp; Margin</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => {
                  const rowKey = tx.id ? `id-${tx.id}` : `ref-${tx.ref}`;
                  const flashTone = tx.ref ? flashedRefs[tx.ref] : null;
                  const typeMeta = getTxTypeMeta(tx.transactionType);
                  const isSelected =
                    (activeTx?.id && activeTx.id === tx.id) ||
                    (activeTx?.ref && activeTx.ref === tx.ref);

                  let rowBg;
                  if (flashTone === 'success') rowBg = 'rgba(16, 185, 129, 0.11)';
                  else if (flashTone === 'error') rowBg = 'rgba(239, 68, 68, 0.09)';
                  else if (flashTone === 'info') rowBg = 'rgba(14, 165, 233, 0.10)';
                  else if (isSelected) rowBg = 'rgba(37, 99, 235, 0.05)';

                  return (
                    <tr
                      key={rowKey}
                      onClick={() => handleOpenDrawer(tx)}
                      style={rowBg ? { backgroundColor: rowBg } : undefined}
                    >
                      <th scope="row" style={rowBg ? { backgroundColor: rowBg } : undefined}>
                        <Stack spacing={0.25}>
                          <Typography fontSize="0.82rem" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                            {tx.createdAt
                              ? moment(tx.createdAt).tz('Africa/Lagos').format('HH:mm:ss')
                              : '—'}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={0.3}>
                            <Typography
                              fontSize="0.72rem"
                              color="text.secondary"
                              sx={{
                                fontFamily: 'monospace',
                                maxWidth: 115,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {tx.ref || `#${tx.id}`}
                            </Typography>
                            <CopyIconBtn value={tx.ref} tooltip="Copy reference" />
                          </Stack>
                        </Stack>
                      </th>

                      <td>
                        {tx.user ? (
                          <Stack spacing={0.15}>
                            <Typography fontSize="0.82rem" fontWeight={700} color="text.primary">
                              @{tx.user.username}
                            </Typography>
                            <Typography fontSize="0.72rem" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                              {tx.user.fullName || tx.user.email || ''}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography fontSize="0.8rem" color="text.secondary">
                            User #{tx.entityTypeId || '—'}
                          </Typography>
                        )}
                      </td>

                      <td>
                        <Stack spacing={0.35}>
                          <Stack direction="row" spacing={0.7} alignItems="center" flexWrap="wrap">
                            <Chip
                              size="small"
                              label={typeMeta.label}
                              sx={{
                                height: 20,
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                bgcolor: alpha(typeMeta.color, 0.1),
                                color: typeMeta.color,
                              }}
                            />
                            <Typography fontSize="0.81rem" fontWeight={600} noWrap sx={{ maxWidth: 210 }}>
                              {tx.serviceName || tx.description || tx.name}
                            </Typography>
                          </Stack>
                          {tx.target ? (
                            <Stack direction="row" alignItems="center" spacing={0.3}>
                              <Typography
                                fontSize="0.74rem"
                                color="text.secondary"
                                sx={{ fontFamily: 'monospace' }}
                              >
                                {tx.target}
                              </Typography>
                              <CopyIconBtn value={tx.target} tooltip="Copy recipient" />
                            </Stack>
                          ) : null}
                        </Stack>
                      </td>

                      <td>
                        <RouteBadge tx={tx} />
                      </td>

                      <td>
                        <Stack spacing={0.2}>
                          <Typography fontSize="0.86rem" fontWeight={800} color="text.primary">
                            ₦{fCurrency(tx.amount || 0)}
                          </Typography>
                          {Number(tx.profit) !== 0 &&
                          (tx.status === 'success' || tx.status === 'completed') ? (
                            <Typography
                              fontSize="0.71rem"
                              fontWeight={700}
                              color={Number(tx.profit) >= 0 ? '#059669' : '#DC2626'}
                            >
                              {Number(tx.profit) >= 0 ? '+' : ''}₦{fCurrency(tx.profit)} margin
                            </Typography>
                          ) : null}
                        </Stack>
                      </td>

                      <td>
                        <LiveStatusChip
                          status={tx.status}
                          manualReconciliationNeeded={tx.manualReconciliationNeeded}
                          refund={tx.refund}
                          liveRouting={tx.liveRouting}
                          reconciliationAttempt={tx.reconciliationAttempt}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        )}
      </Paper>

      {/* ─── Slide-Over Live Inspector Drawer ─────────────────────────── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480 },
            p: 0,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Sticky Drawer Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: '1px solid #E2E8F0',
            bgcolor: '#FFFFFF',
            position: 'sticky',
            top: 0,
            zIndex: 5,
          }}
        >
          <Stack spacing={0.2}>
            <Typography fontWeight={800} fontSize="1.05rem">
              Live Transaction Inspector
            </Typography>
            <Typography fontSize="0.75rem" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {activeTx?.ref || activeTx?.txRef || ''}
            </Typography>
          </Stack>
          <IconButton onClick={handleCloseDrawer} size="small" sx={{ bgcolor: '#F1F5F9' }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
          {detailLoading && !selectedDetail && (
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: '14px' }} />
              <Skeleton variant="rounded" height={180} sx={{ borderRadius: '14px' }} />
              <Skeleton variant="rounded" height={220} sx={{ borderRadius: '14px' }} />
            </Stack>
          )}
          {(!detailLoading || selectedDetail) && Boolean(activeTx) && (
            <>
              {/* Hero Summary */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 2.5,
                  borderRadius: '16px',
                  border: '1px solid #E2E8F0',
                  background: 'linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%)',
                  textAlign: 'center',
                }}
              >
                <Chip
                  size="small"
                  label={getTxTypeMeta(activeTx.transactionType || activeTx.category).label}
                  sx={{
                    mb: 1,
                    fontWeight: 700,
                    bgcolor: alpha(getTxTypeMeta(activeTx.transactionType).color, 0.1),
                    color: getTxTypeMeta(activeTx.transactionType).color,
                  }}
                />
                <Typography fontSize="1.85rem" fontWeight={800}>
                  ₦{fCurrency(activeTx.amount || 0)}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <LiveStatusChip
                    status={activeTx.status || 'pending'}
                    manualReconciliationNeeded={activeTx.manualReconciliationNeeded}
                    refund={activeTx.refund}
                    liveRouting={activeTx.liveRouting}
                    reconciliationAttempt={activeTx.reconciliationAttempt}
                  />
                </Box>
                {activeTx.createdAt ? (
                  <Typography fontSize="0.78rem" color="text.secondary" sx={{ mt: 1 }}>
                    {moment(activeTx.createdAt).tz('Africa/Lagos').format('MMM DD, YYYY · HH:mm:ss')} WAT
                  </Typography>
                ) : null}
              </Paper>

              {/* Manual Reconciliation Alert */}
              {activeTx.manualReconciliationNeeded && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.8,
                    mb: 2.5,
                    borderRadius: '12px',
                    bgcolor: '#FFFBEB',
                    border: '1px solid #FCD34D',
                  }}
                >
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <WarningAmberRounded sx={{ color: '#D97706', fontSize: 20, mt: 0.2 }} />
                    <Box>
                      <Typography fontSize="0.82rem" fontWeight={800} color="#92400E">
                        Manual Reconciliation Required
                      </Typography>
                      <Typography fontSize="0.78rem" color="#B45309" sx={{ mt: 0.3 }}>
                        {activeTx.reconciliationNote ||
                          'Upstream vendor status was ambiguous. Wallet debit is held safely; verify with vendor before refunding or completing.'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}

              {/* Vendor Route Execution Timeline */}
              {routeTimeline.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: '14px',
                    border: '1px solid #E2E8F0',
                    mb: 2.5,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ px: 2, py: 1.3, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                    <Typography fontSize="0.84rem" fontWeight={800}>
                      Vendor Route Execution ({routeTimeline.length})
                    </Typography>
                  </Box>
                  <Stack divider={<Divider />} sx={{ px: 2 }}>
                    {routeTimeline.map((step, idx) => {
                      const stepStatus = String(step.status || '').toUpperCase();
                      return (
                        <Stack key={`${step.processorSlug}-${idx}`} spacing={0.5} sx={{ py: 1.4 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={0.8} alignItems="center">
                              {renderTimelineIcon(step)}
                              <Typography fontSize="0.83rem" fontWeight={700}>
                                #{idx + 1} · {step.providerName || step.processorSlug}
                              </Typography>
                              {step.isFailover && (
                                <Chip
                                  size="small"
                                  label="Failover"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    bgcolor: '#FEF3C7',
                                    color: '#B45309',
                                  }}
                                />
                              )}
                            </Stack>
                            <Typography fontSize="0.75rem" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                              {step.durationMs !== undefined && step.durationMs !== null
                                ? `${step.durationMs}ms`
                                : stepStatus}
                            </Typography>
                          </Stack>
                          {step.errorMessage && (
                            <Typography fontSize="0.74rem" color="#DC2626" sx={{ pl: 2.8 }}>
                              {step.errorMessage}
                            </Typography>
                          )}
                          {step.requestPayload && (
                            <Box sx={{ pl: 2.8 }}>
                              <AttemptPayloadViewer payload={step.requestPayload} />
                            </Box>
                          )}
                        </Stack>
                      );
                    })}
                  </Stack>
                </Paper>
              )}

              {/* RBAC Admin Actions */}
              {isOrderEligible && (
                <Stack direction="row" spacing={1.2} sx={{ mb: 2.5 }}>
                  {hasPermission(adminUser, PERMISSIONS.TRANSACTIONS_REFUND) && (
                    <LoadingButton
                      loading={refundLoading}
                      disabled={Boolean(activeTx.refund)}
                      onClick={handleRefund}
                      color="error"
                      variant="outlined"
                      fullWidth
                      sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                    >
                      {activeTx.refund ? '✓ Refunded' : 'Refund Customer'}
                    </LoadingButton>
                  )}
                  {(activeTx.isManual || activeTx.manualReconciliationNeeded) &&
                    activeTx.status === 'pending' &&
                    hasPermission(adminUser, PERMISSIONS.TRANSACTIONS_MANUAL_UPDATE) && (
                      <LoadingButton
                        loading={completeLoading}
                        onClick={handleManualComplete}
                        color="success"
                        variant="contained"
                        fullWidth
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                      >
                        Mark Success
                      </LoadingButton>
                    )}
                </Stack>
              )}

              {/* Customer Identity Card */}
              {(activeTx.user || activeTx.entityTypeId || activeTx.userId) && (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: '14px',
                    border: '1px solid #E2E8F0',
                    mb: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ px: 2, py: 1.2, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}
                  >
                    <Typography fontSize="0.82rem" fontWeight={800}>
                      Customer
                    </Typography>
                    {(activeTx.user?.id || activeTx.entityTypeId || activeTx.userId) && (
                      <Button
                        component={RouterLink}
                        to={`/dashboard/user-details/${
                          activeTx.user?.id || activeTx.entityTypeId || activeTx.userId
                        }`}
                        size="small"
                        endIcon={<OpenInNew sx={{ fontSize: '14px !important' }} />}
                        sx={{ textTransform: 'none', fontSize: '0.76rem', fontWeight: 700, py: 0 }}
                      >
                        Profile
                      </Button>
                    )}
                  </Stack>
                  <DetailRow
                    label="Username"
                    value={activeTx.user?.username ? `@${activeTx.user.username}` : `User #${activeTx.entityTypeId || activeTx.userId}`}
                  />
                  {activeTx.user?.fullName && <DetailRow label="Full Name" value={activeTx.user.fullName} />}
                  {activeTx.user?.email && <DetailRow label="Email" value={activeTx.user.email} copyable />}
                  {activeTx.user?.phone && <DetailRow label="Phone" value={activeTx.user.phone} mono copyable />}
                </Paper>
              )}

              {/* Financial & Margin Breakdown */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '14px',
                  border: '1px solid #E2E8F0',
                  mb: 2,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 2, py: 1.2, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                  <Typography fontSize="0.82rem" fontWeight={800}>
                    Financial &amp; Margin Breakdown
                  </Typography>
                </Box>
                <DetailRow label="Amount Charged" value={`₦${fCurrency(activeTx.amount || 0)}`} />
                {activeTx.faceValue ? (
                  <DetailRow label="Face Value" value={`₦${fCurrency(activeTx.faceValue)}`} />
                ) : null}
                {Number(activeTx.discountAmount) > 0 ? (
                  <DetailRow label="Discount" value={`-₦${fCurrency(activeTx.discountAmount)}`} />
                ) : null}
                {Number(activeTx.fee) > 0 ? (
                  <DetailRow label="Fee" value={`₦${fCurrency(activeTx.fee)}`} />
                ) : null}
                {Number(activeTx.buyingAmount) > 0 ? (
                  <DetailRow label="Vendor Cost" value={`₦${fCurrency(activeTx.buyingAmount)}`} />
                ) : null}
                {Number(activeTx.profit) !== 0 &&
                (activeTx.status === 'success' || activeTx.status === 'completed') ? (
                  <DetailRow
                    label="Net Margin"
                    value={`${Number(activeTx.profit) >= 0 ? '+' : ''}₦${fCurrency(activeTx.profit)}`}
                    valueColor={Number(activeTx.profit) >= 0 ? '#059669' : '#DC2626'}
                  />
                ) : null}
                {activeTx.balanceBefore !== undefined && (
                  <>
                    <Divider sx={{ mx: 1.5 }} />
                    <DetailRow label="Wallet Before" value={`₦${fCurrency(activeTx.balanceBefore || 0)}`} />
                    <DetailRow label="Wallet After" value={`₦${fCurrency(activeTx.balanceAfter || 0)}`} />
                  </>
                )}
              </Paper>

              {/* Identifiers & Fulfillment */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '14px',
                  border: '1px solid #E2E8F0',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 2, py: 1.2, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                  <Typography fontSize="0.82rem" fontWeight={800}>
                    Order &amp; Fulfillment Metadata
                  </Typography>
                </Box>
                <DetailRow label="Reference" value={activeTx.ref || activeTx.txRef} mono copyable />
                {activeTx.txId && <DetailRow label="Tx ID" value={activeTx.txId} mono copyable />}
                {activeTx.clientRef && <DetailRow label="Client Ref" value={activeTx.clientRef} mono copyable />}
                {activeTx.accountRef && <DetailRow label="Account Ref" value={activeTx.accountRef} mono copyable />}
                {activeTx.vendorRef && <DetailRow label="Vendor Ref" value={activeTx.vendorRef} mono copyable />}
                {activeTx.target && <DetailRow label="Recipient / Target" value={activeTx.target} mono copyable />}
                {activeTx.providerSlug && <DetailRow label="Winning Provider" value={activeTx.providerSlug} mono />}
                {activeTx.planId && <DetailRow label="Plan ID" value={activeTx.planId} mono />}
                {activeTx.token && <DetailRow label="Token / PIN" value={activeTx.token} mono copyable />}
                {activeTx.tokenUnits && <DetailRow label="Electricity Units" value={activeTx.tokenUnits} />}
                {activeTx.statusMessage && <DetailRow label="Status Message" value={activeTx.statusMessage} />}
              </Paper>

              {/* External Verification Button */}
              {canVerifyExternally && !externalResult && (
                <LoadingButton
                  loading={verifyLoading}
                  onClick={handleVerifyExternally}
                  variant="outlined"
                  fullWidth
                  startIcon={<VerifiedOutlined />}
                  sx={{
                    mt: 2,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    borderColor: '#CBD5E1',
                    color: 'text.primary',
                  }}
                >
                  Verify Externally with Provider
                </LoadingButton>
              )}

              {externalResult && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 2,
                    borderRadius: '14px',
                    border: '1px solid #BAE6FD',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ px: 2, py: 1.2, bgcolor: '#F0F9FF', borderBottom: '1px solid #BAE6FD' }}>
                    <Typography fontSize="0.82rem" fontWeight={800} color="#0369A1">
                      External Provider Verification
                    </Typography>
                  </Box>
                  <DetailRow label="Payment Status" value={externalResult.paymentStatus} />
                  <DetailRow label="Method" value={externalResult.paymentMethod} />
                  <DetailRow label="Reference" value={externalResult.transactionReference} mono copyable />
                  <DetailRow label="Amount Paid" value={externalResult.amountPaid} />
                </Paper>
              )}

              {/* Sanitized Outbound Request & Inbound Vendor JSON Viewer */}
              <ApiResponseViewer
                response={activeTx.response_data}
                requestPayload={activeTx.request_payload || activeTx.response_data?.request_payload}
              />
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}

